/**
 * Contexte de session : identité, entreprise courante, état de la 2FA.
 *
 * L'état est reconstruit depuis `GET /auth/session` au chargement : le
 * navigateur ne conserve aucune information d'authentification, c'est le cookie
 * HttpOnly qui fait autorité.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ApiError, setCurrentBusinessId } from "../api/client";
import { authApi } from "../api/endpoints";
import type { BusinessSummary, SessionState, UserInfo } from "../api/types";
import { configureFormatting } from "../lib/format";

export interface SessionContextValue {
  /** `null` tant que l'état n'a pas été résolu au premier chargement. */
  state: SessionState | null;
  loading: boolean;
  user: UserInfo | null;
  businesses: BusinessSummary[];
  currentBusiness: BusinessSummary | null;
  isAuthenticated: boolean;
  needsTwoFactor: boolean;
  needsEmailVerification: boolean;
  needsOnboarding: boolean;
  reauthValidUntil: Date | null;
  hasFreshReauth: boolean;
  refresh: () => Promise<SessionState | null>;
  applyState: (state: SessionState) => void;
  selectBusiness: (businessId: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState | null>(null);
  const [loading, setLoading] = useState(true);

  const applyState = useCallback((next: SessionState) => {
    setState(next);
    if (next.user) {
      configureFormatting({
        locale: next.user.locale,
        timeZone: next.user.timezone,
      });
    }
    if (next.current_business_id) {
      setCurrentBusinessId(next.current_business_id);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const next = await authApi.session();
      applyState(next);
      return next;
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setState(null);
        setCurrentBusinessId(null);
        return null;
      }
      // Une panne réseau ne doit pas déconnecter l'utilisateur : on garde
      // l'état courant et l'écran affichera l'erreur.
      if (!(error instanceof ApiError) || error.code !== "network_error") {
        setState(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyState]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectBusiness = useCallback(
    async (businessId: string) => {
      setCurrentBusinessId(businessId);
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* même en cas d'échec réseau, on nettoie l'état local */
    }
    setCurrentBusinessId(null);
    setState(null);
  }, []);

  const value = useMemo<SessionContextValue>(() => {
    const user = state?.user ?? null;
    const businesses = state?.businesses ?? [];
    const currentBusiness =
      businesses.find((b) => b.id === state?.current_business_id) ??
      businesses[0] ??
      null;
    const reauthValidUntil = state?.reauth_valid_until
      ? new Date(
          state.reauth_valid_until.endsWith("Z")
            ? state.reauth_valid_until
            : `${state.reauth_valid_until}Z`,
        )
      : null;

    return {
      state,
      loading,
      user,
      businesses,
      currentBusiness,
      isAuthenticated: Boolean(state?.authenticated && state.twofa_satisfied),
      needsTwoFactor: Boolean(state?.authenticated && !state.twofa_satisfied),
      needsEmailVerification: Boolean(user && !user.email_verified),
      needsOnboarding: Boolean(
        state?.authenticated && state.twofa_satisfied && businesses.length === 0,
      ),
      reauthValidUntil,
      hasFreshReauth: Boolean(
        reauthValidUntil && reauthValidUntil.getTime() > Date.now(),
      ),
      refresh,
      applyState,
      selectBusiness,
      signOut,
    };
  }, [state, loading, refresh, applyState, selectBusiness, signOut]);

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession doit être utilisé dans un SessionProvider.");
  }
  return context;
}
